import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import arraySupport from 'dayjs/plugin/arraySupport';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEffect, useState } from 'react';
import MyDatePicker from './MyDatePicker';
import {
  ClientDataContainerProps, DataHour, DataLabels, ClientSubmit, Client,
} from './Interfaces';
import Select from 'react-select';
import { HiOutlineClipboardDocumentCheck } from "react-icons/hi2";
import Popup from 'reactjs-popup';
import { IoIosAlert } from "react-icons/io";
import { ClipLoader } from 'react-spinners';
import ReCAPTCHA from "react-google-recaptcha";
import { sleep } from '../utils/utils';
import Checkbox from '@mui/material/Checkbox';

const nestApiURL = process.env.REACT_APP_NEST_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;
const platformId = process.env.REACT_APP_PLATFORM_ID || 1;
const mailTemplateAgendamiento = process.env.REACT_APP_MAIL_TEMPLATE_AGENDAMIENTO || 9;
const mailSection = process.env.REACT_APP_MAIL_SECTION || 1;
const authorization = process.env.REACT_APP_AUTHORIZATION;

const phoneFormatter = (phone: string) => {
  if (phone?.includes('+569')) {
    return phone.slice(4);
  } if (phone?.length === 9) {
    return phone.slice(1);
  } if (phone?.length > 9 && phone?.includes('569')) {
    return phone.slice(3);
  }
  if (!phone) {
    return '';
  }
  return phone;
};

const dateFormatter = (date: string) => {
  dayjs.extend(arraySupport);
  if (date === '') {
    return dayjs().toDate();
  }
  if (date === undefined) {
    return dayjs().toDate();
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date?.split('-');
    return dayjs([+year, +month - 1, +day]).toDate();
  }

  const [day, month, year] = date?.split('-');
  return dayjs([+year, +month - 1, +day]).toDate();
};

const dateFormatterNotification = (date: any) => {
  const fecha = dayjs(date).locale('es');

  const hora = fecha.format('HH');
  const minuto = fecha.format('mm');
  const dia = fecha.format('dddd');
  const month = fecha.format('MMMM');
  const dayNumber = fecha.date();
  const capitalizedDay = dia.charAt(0).toUpperCase() + dia.slice(1);
  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

  const reservationDate = `${capitalizedDay} ${dayNumber} de ${capitalizedMonth}`;
  const reservationHour = `${hora}:${minuto}`;

  return { reservationDate, reservationHour };
};

const isValidNumber = (val: any) => {
  if (val === null || val === undefined || val === '') return false;
  return !isNaN(Number(val));
};

const ClientDataContainer = ({
  clientData,
  clientExists,
  dataHour,
  dataLabels,
}: ClientDataContainerProps) => {
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [previsiones, setPrevisiones] = useState<any[]>([]);
  const [regiones, setRegiones] = useState<any[]>([]);
  const [comunas, setComunas] = useState<any[]>([]);
  const [dataISOcountries, setDataISOcountries] = useState<any[]>([]);
  const navigate = useNavigate();

  const { reservationDate, reservationHour } = dateFormatterNotification(dataHour.reservationHour);
  const [verificarMail, setVerificarMail] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validatingMail, setValidatingMail] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [repeatedCodeHour, setRepeatedCodeHour] = useState<any>(null);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [userPermission, setUserPermission] = useState<boolean>(true);
  const [emailVerified, setEmailVerified] = useState<boolean>(clientData.confirmacion_correo === 1);
  const initialEmail = clientData.correo || (clientData as any).email || '';
  const [currentEmail, setCurrentEmail] = useState<string>(initialEmail);
  const [verificationStep, setVerificationStep] = useState<'initial' | 'code_sent' | 'verified'>(clientData.confirmacion_correo === 1 ? 'verified' : 'initial');
  const [canResend, setCanResend] = useState<boolean>(true);
  const [resendTimer, setResendTimer] = useState<number>(0);
  
  const [otpError, setOtpError] = useState<string>("");
  const [isValidatingOTP, setIsValidatingOTP] = useState<boolean>(false);

  const ReservationSchema = Yup.object().shape({
    nombre: Yup.string().required('Campo requerido'),
    paterno: Yup.string().required('Campo requerido'),
    materno: Yup.string().required('Campo requerido'),
    nacimiento: Yup.date()
      .required('Campo requerido')
      .max(dayjs().subtract(2, 'day').toDate(), 'La fecha de nacimiento no es válida')
      .min(new Date('1925-01-01'), 'La fecha de nacimiento no es válida')
      .typeError('La fecha de nacimiento no es válida'),
    correo: Yup.string()
      .email('Correo inválido')
      .required('Campo requerido'),
    direccion: Yup.string().required('Direccion requerida'),
    region: Yup.number().required('Campo requerido').typeError('Campo requerido'),
    comuna: Yup.number().required('Campo requerido').typeError('Campo requerido'),
    prevision: Yup.number().required('Campo requerido').typeError('Campo requerido'),
    nacionalidad: Yup.number().required('Campo requerido').typeError('Campo requerido'),
    ocupacion: Yup.number().required('Campo requerido').typeError('Campo requerido'),
    sexo: Yup.string().required('Campo requerido'),
    celular: Yup.number()
      .required('Campo requerido')
      .min(10000000, 'El número debe ser de 8 dígitos')
      .max(99999999, 'El número debe ser de 8 dígitos')
      .typeError('Por favor ingresa números solamente'),
    verificador: Yup.string(),
    concentimiento: Yup.boolean().required('Campo requerido')
  });

  const sendOTP = async (email: string) => {
    try {
      setOtpError("");
      const payload = {
        email: email,
        nombre: `${clientData.nombre || ''} ${clientData.paterno || ''}`.trim(),
        rut: clientData.rut
      };
      
      await axios.post(`${nestApiURL}/mail/otp/send`, payload, {
        headers: {
          'API_KEY': apiKey,
          'Authorization': authorization,
          'Content-Type': 'application/json'
        },
      });
    } catch (error) {
      console.error("Error al solicitar código OTP:", error);
    }
  };

  const verifyOTP = async (codigo: string, email: string) => {
    setIsValidatingOTP(true);
    setOtpError("");
    
    try {
      const res = await axios.post(`${nestApiURL}/mail/otp/validate`, {
        email: email,
        codigo: codigo,
        rut: clientData.rut
      }, {
        headers: {
          'API_KEY': apiKey,
          'Authorization': authorization,
          'Content-Type': 'application/json'
        }
      });

      if (res?.data?.status === 'success') {
        setEmailVerified(true);
        setVerificationStep('verified');
        setVerificarMail(true);
      } else {
        setOtpError("El código es incorrecto o ha expirado");
      }
    } catch (err) {
      setOtpError("El código es incorrecto o ha expirado");
    } finally {
      setIsValidatingOTP(false);
    }
  };

  const verifaliaEmail = async (email: string) => {
    if (!email) return false;
    setValidatingMail(true);
    
    try {
      const payload = {
        email: email,
        rut: clientData.rut, 
        platform_id: Number(platformId)
      };

      const response = await axios.post(`${nestApiURL}/patients/verify-email`, payload, {
        headers: {
          'API_KEY': apiKey,
          'Content-Type': 'application/json',
        },
      });
      
      if (response?.data?.status === 'success') {
        setCurrentEmail(email);
        setValidatingMail(false);
        return true;
      } else {
        throw new Error("Inválido");
      }
    } catch (err) {
      setValidatingMail(false);
      return false;
    }
  }

  const toHome = () => { window.location.replace('https://maiposalud.cl'); };
  const toReserve = () => { navigate('/filter', { state: { message: `${dataHour.idGroupConsultHour}`, clientData } }); };
  
  const cancelarHora = () => {
    setIsCancelling(true);

    axios.post(`${nestApiURL}/appointments/confirmation`,
      {
        id_agendamiento: Number(repeatedCodeHour),
        status: 2, 
        rut: clientData.rut,
        platform_id: Number(platformId)
      },
      {
        headers: {
          'API_KEY': apiKey,
          'Content-Type': 'application/json',
        },
      })
      .then(() => {
        setIsCancelling(false);
        navigate('/cancel-reservation', { state: { id: `${dataHour.idGroupConsultHour}`, clientData } });
      })
      .catch((err) => { 
        setIsCancelling(false);
        console.error("Error al anular la hora:", err);
        alert("Hubo un error al intentar anular la hora. Por favor, inténtalo de nuevo.");
      });
  }

  const reserveHour = async (
    dataHourF: DataHour,
    clientDataF: ClientSubmit,
    dataLabelsF: DataLabels,
  ) => {
    const [fecha, hora] = dataHourF.reservationHour.split(' ');

    const bodyNest = {
      rut: clientDataF.rut,
      professional_id: Number(dataHourF.idProfesional),
      date: fecha,
      hour: hora,
      facility_id: Number(dataHourF.facility),
      categorie_id: Number(dataHourF.categorie),
      platform_id: Number(platformId), 
      user_id: dataHourF.uid ? Number(dataHourF.uid) : null,
    };

    setIsLoading(true);

    try {
      const facResponse = await axios.get(`${nestApiURL}/facilities`, { headers: { 'API_KEY': apiKey } });
      if (facResponse?.data?.status === 'success' && Array.isArray(facResponse?.data?.data)) {
        const facility = facResponse.data.data.find((item: any) => String(item.id) === String(dataHourF.facility));
        if (facility) {
          dataLabelsF.facility.street = facility.street || '';
          dataLabelsF.facility.state = facility.state || '';
        }
      }
    } catch (e) {
      // Ignorar error de facility
    }

    try {
      const response = await axios.post(`${nestApiURL}/appointments`, bodyNest, {
        headers: {
          'API_KEY': apiKey,
          'Content-Type': 'application/json',
        },
      });

      const eventId = response?.data?.data?.event_id;
      dataHourF.reservationCode = eventId ? String(eventId) : ''; 
      
      if (response?.data?.status === 'success' && eventId) {
        const mailPayload = {
          referenciaId: Number(eventId),
          plantillaId: Number(mailTemplateAgendamiento),
          seccion: Number(mailSection)
        };

        axios.post(`${nestApiURL}/mail/trigger`, mailPayload, {
          headers: {
            'API_KEY': apiKey,
            'Content-Type': 'application/json'
          }
        }).catch((mailError) => {
          console.error("La cita se creó, pero el trigger del correo falló:", mailError);
        });
      }
      
      const prevision: any = previsiones.find((item: any) => String(item.codigo) === String(clientDataF.prevision));
      const previsionName = prevision ? prevision.nombre : 'Sin previsión';
      
      setIsLoading(false);
      navigate('/reservation-data', {
        state: {
          dataHour: dataHourF,
          clientData: clientDataF,
          dataLabels: dataLabelsF,
          reservationDate,
          reservationHour,
          previsionName,
        },
      });
    } catch (err: any) {
      setIsLoading(false);
      const errorMessage = err.response?.data?.message || '';
      
      if (errorMessage.includes('ya tienes una cita para la misma prestación')) {
        setOpenModal(true);
        const match = errorMessage.match(/\(ID:\s*(\d+)\)/);
        if (match && match[1]) {
          setRepeatedCodeHour(match[1]);
        }
      } else {
        console.error("Error al agendar:", err);
        alert("Ocurrió un error al agendar la hora. Por favor, verifica tus datos e inténtalo nuevamente.");
      }
    }
  };

  const createUser = async (
    dataHourF: DataHour,
    clientDataF: ClientSubmit,
    dataLabelsF: DataLabels,
  ) => {
    
    let finalRut = clientDataF.rut;
    let pasaporteValue = null;
    let finalCountryId = Number(clientDataF.nacionalidad); // Por defecto el ID seleccionado

    // Lógica para extranjeros
    if (clientDataF.pasaporte) {
      // 1. Encontrar el país seleccionado por su ID
      const country = dataISOcountries.find((c: any) => String(c.id) === String(clientDataF.nacionalidad));
      
      if (country) {
        const isonum = country.isonum;
        // 2. Concatenar el documento limpio (SS) con el isonum
        finalRut = `${clientDataF.ss}${isonum}`;
        // 3. Guardar el documento crudo en el campo pasaporte
        pasaporteValue = clientDataF.ss;
        // 4. Asegurar que enviamos el ID correcto a la base de datos
        finalCountryId = Number(country.id); 
      }
    }

    const bodyNest = {
      rut: finalRut,
      nombre: clientDataF.nombre,
      paterno: clientDataF.paterno,
      materno: clientDataF.materno || null,
      direccion: clientDataF.direccion,
      correo: clientDataF.correo,
      celular: `+569${phoneFormatter(clientDataF.celular).replace(/\s+/g, '')}`,
      fecha_nacimiento: clientDataF.fechaNacimiento,
      sexo: clientDataF.sexo,
      prevision: Number(clientDataF.prevision),
      nacionalidad: finalCountryId, // Aquí enviamos el ID correcto garantizado
      region: Number(clientDataF.region),
      comuna: Number(clientDataF.comuna), 
      ocupacion: Number(clientDataF.ocupacion),
      pasaporte: pasaporteValue,
      plataforma_id: Number(platformId)
    };

    try {
      await axios.post(`${nestApiURL}/patients`, bodyNest, {
        headers: {
          'API_KEY': apiKey,
          'Content-Type': 'application/json',
        },
      });
      
      clientDataF.rut = finalRut;
      await reserveHour(dataHourF, clientDataF, dataLabelsF);
      
    } catch (err) {
      setIsLoading(false);
      console.error("Error creating patient", err);
      alert("Error al crear el registro del paciente. Revisa que los datos ingresados sean correctos.");
    }
  };

  const isUserChange = (clientOriginal: Client, clientUpdate: ClientSubmit) => {
    if (clientOriginal.nombre !== clientUpdate.nombre) return true;
    if (clientOriginal.paterno !== clientUpdate.paterno) return true;
    if (clientOriginal.materno !== clientUpdate.materno) return true;
    if (clientOriginal.direccion !== clientUpdate.direccion) return true;
    
    const origRegion = String((clientOriginal as any).region || (clientOriginal as any).city || '');
    const origComuna = String((clientOriginal as any).comuna || (clientOriginal as any).state || '');
    if (origRegion !== String(clientUpdate.region)) return true;
    if (origComuna !== String(clientUpdate.comuna)) return true;

    const origCorreo = clientOriginal.correo || (clientOriginal as any).email || '';
    if (origCorreo !== clientUpdate.correo) return true;

    if (clientOriginal.sexo !== clientUpdate.sexo) return true;
    if (clientOriginal.fechaNacimiento !== clientUpdate.fechaNacimiento) return true;
    if (String(clientOriginal.prevision) !== String(clientUpdate.prevision)) return true;
    
    const origCelular = phoneFormatter(clientOriginal.celular).replace(/\s+/g, '');
    const newCelular = String(clientUpdate.celular).replace(/\s+/g, '');
    if (origCelular !== newCelular) return true;
    
    if (String(clientOriginal.nacionalidad) !== String(clientUpdate.nacionalidad)) return true;
    if (String(clientOriginal.ocupacion) !== String(clientUpdate.ocupacion)) return true;
    if (clientOriginal.concentimiento !== clientUpdate.concentimiento) return true;

    return false;
  };

  const updateUser = async (
    dataHourF: DataHour,
    clientDataF: ClientSubmit,
    dataLabelsF: DataLabels,
  ) => {
    let finalCountryId = Number(clientDataF.nacionalidad);
    
    if (clientDataF.pasaporte) {
      const country = dataISOcountries.find((c: any) => String(c.id) === String(clientDataF.nacionalidad));
      if (country) {
        finalCountryId = Number(country.id);
      }
    }

    const bodyNest = {
      nombre: clientDataF.nombre,
      paterno: clientDataF.paterno,
      materno: clientDataF.materno || null,
      direccion: clientDataF.direccion,
      correo: clientDataF.correo,
      celular: `+569${phoneFormatter(clientDataF.celular).replace(/\s+/g, '')}`, 
      fecha_nacimiento: clientDataF.fechaNacimiento,
      sexo: clientDataF.sexo,
      prevision: Number(clientDataF.prevision),
      nacionalidad: finalCountryId, // Actualiza con el ID correcto garantizado
      region: Number(clientDataF.region), 
      comuna: Number(clientDataF.comuna),
      ocupacion: Number(clientDataF.ocupacion),
      plataforma_id: Number(platformId)
    };

    try {
      await axios.put(`${nestApiURL}/patients/${clientDataF.rut}`, bodyNest, {
        headers: {
          'API_KEY': apiKey,
          'Content-Type': 'application/json',
        },
      });
      await reserveHour(dataHourF, clientDataF, dataLabelsF);
    } catch (err) {
      setIsLoading(false);
      console.error("Error updating patient", err);
      alert("Error al actualizar los datos del paciente. Revisa que los datos ingresados sean correctos.");
    }
  };

  const fetchCommunesByRegion = async (regionId: string | number) => {
    try {
      const response = await axios.get(`${nestApiURL}/communes/${regionId}`, {
        headers: { 'API_KEY': apiKey },
      });
      if (response?.data?.status === 'success') {
        setComunas(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching communes", error);
    }
  };

  useEffect(() => {
    if (!dataLoaded) {
      axios.get(`${nestApiURL}/financers`, {
        headers: { 'API_KEY': apiKey },
      }).then((response: any) => {
        if (response?.data?.status === 'success') {
          setPrevisiones(response.data.data);
        }
      }).catch(() => {});

      axios.get(`${nestApiURL}/regions`, {
        headers: { 'API_KEY': apiKey },
      }).then((response: any) => {
        if (response?.data?.status === 'success') {
          setRegiones(response.data.data);
          
          const clientRegion = (clientData as any).region || (clientData as any).city;
          if (isValidNumber(clientRegion)) {
            fetchCommunesByRegion(clientRegion);
          }
        }
      }).catch(() => {});

      axios.get(`${nestApiURL}/countries`, {
        headers: { 'API_KEY': apiKey },
      }).then((response: any) => {
        if (response?.data?.status === 'success') {
          setDataISOcountries(response.data.data);
        }
      }).catch(() => {});

      setDataLoaded(true);
    }
  }, [dataLoaded, clientData]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [resendTimer]);

  const colourStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'white',
      border: state.isFocused ? 0 : 0,
      boxShadow: state.isFocused ? 0 : 0,
      '&:hover': {
        border: state.isFocused ? 0 : 0,
      },
      fontWeight: 'lighter',
      fontSize: '14px',
    }),
    menu: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'white',
      border: state.isFocused ? 0 : 0,
      boxShadow: state.isFocused ? 0 : 0,
      '&:hover': {
        border: state.isFocused ? 0 : 0,
      },
      fontWeight: 'lighter',
      fontSize: '14px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#80808060',
      fontWeight: 'lighter',
      fontSize: '14px',
    }),
  };

  const PopUp = () => {
    return (
      <Popup open={openModal} modal closeOnDocumentClick={false}>
        <div className='popUpContainer'>
          <div style={{ display: 'flex', flexDirection: 'row', width: '25%', paddingTop: '3%' }}>
            <IoIosAlert size={40} color='#52A92B' />
            <p className='popUpTitle'>Alerta</p>
          </div>
          <p className='popUpSubtitle'>Lo sentimos. No es posible concretar tu reserva, porque ya tienes una cita para la misma especialidad. El código de su reserva es {repeatedCodeHour}.</p>
          <div className="buttons">
            <div style={{ width: '100%' }}>
              <button disabled={isCancelling} className='containerTopButton' type="submit" onClick={toReserve}>Reservar otra hora</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', }}>
              <button disabled={isCancelling} className='containerBottomButton' type="submit" onClick={toHome}>Volver al inicio</button>
              <button disabled={isCancelling} className='containerBottomButton' type="submit" onClick={cancelarHora}>
                {isCancelling ? <ClipLoader size={14} color="#78cd51" /> : 'Anular hora'}
              </button>
            </div>
          </div>
        </div>
      </Popup>
    )
  }

  const handleChangeCaptcha = (value: any) => {
    if (value) {
      setCaptcha(value);
    } else {
      setCaptcha(null);
    }
  }

  const handleCaptchaExpired = () => {
    setCaptcha(null);
    setIsLoading(false);
  }

  const handleCaptchaError = () => {
    setCaptcha(null);
    setIsLoading(false);
  }

  const handleEmailValidation = async (email: string) => {
    setErrorMessage("");
    if (verificationStep === 'verified') return;

    const isValid = await verifaliaEmail(email);
    if (isValid) {
      if (email === initialEmail) {
        setEmailVerified(true);
        setVerificationStep('verified');
        setVerificarMail(true);
      } else {
        setVerificationStep('code_sent');
        setCanResend(false);
        setResendTimer(60);
        await sendOTP(email);
      }
    } else {
      setErrorMessage("El correo no es válido");
    }
  };

  const ValidateEmailButton = ({ email }: { email: string }) => {
    const isVerified = verificationStep === 'verified';
    const isCodeSent = verificationStep === 'code_sent';

    return (
      <button
        type="button"
        onClick={() => handleEmailValidation(email)}
        disabled={validatingMail || !email || isVerified || !canResend}
        style={{
          padding: '8px 12px',
          backgroundColor: isVerified ? '#cccccc' : validatingMail || !canResend ? '#cccccc' : '#52A92B',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isVerified || validatingMail || !email || !canResend ? 'not-allowed' : 'pointer',
          marginLeft: '8px',
          marginBottom: '8px',
          fontSize: '12px',
          fontWeight: '600',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '120px',
          height: '36px'
        }}
      >
        {validatingMail ? (
          <ClipLoader size={14} color="#ffffff" />
        ) : isVerified ? (
          <>
            <span style={{ marginRight: '6px' }}>✓</span>
            Verificado
          </>
        ) : isCodeSent ? (
          resendTimer > 0 ? `Reenviar en ${resendTimer}s` : 'Reenviar código'
        ) : (
          <>
            <span style={{ marginRight: '6px' }}>✓</span>
            Validar correo
          </>
        )}
      </button>
    );
  };

  return (
    <div className="clientDataContainerH">
      <p className="containerTitleReservation">
        <HiOutlineClipboardDocumentCheck style={{ marginRight: '4%' }} size={34} />
        Reserva de hora
      </p>
      <p className="containerSubTitle">
        {'Por favor ingrese sus datos para continuar con el agendamiento'}
      </p>
     <div className="clientInputDataContainer">
        <Formik
          initialValues={{
            nombre: (clientData.nombre ? clientData.nombre : ''),
            paterno: (clientData.paterno ? clientData.paterno : ''),
            materno: (clientData.materno ? clientData.materno : ''),
            nacimiento: dateFormatter(clientData.fechaNacimiento),
            sexo: (clientData.sexo ? clientData.sexo : 'M'),
            prevision: isValidNumber(clientData.prevision) ? clientData.prevision : '',
            direccion: (clientData.direccion ? clientData.direccion : ''),
            region: isValidNumber((clientData as any).region) ? (clientData as any).region : (isValidNumber((clientData as any).city) ? (clientData as any).city : ''),
            comuna: isValidNumber((clientData as any).comuna) ? (clientData as any).comuna : (isValidNumber((clientData as any).state) ? (clientData as any).state : ''),
            correo: (initialEmail ? initialEmail : ''),
            // Forzamos a String para que react-select lo lea correctamente
            nacionalidad: isValidNumber(clientData.nacionalidad) ? String(clientData.nacionalidad) : '',
            celular: `${phoneFormatter(clientData.celular).replace(/\s+/g, '')}`,
            edad: (clientData.edad ? clientData.edad : ''),
            fechaNacimiento: '',
            verificador: '',
            ocupacion: isValidNumber(clientData.ocupacion) ? clientData.ocupacion : '',
            concentimiento: userPermission,
            ss: (clientData.ss ? clientData.ss : ''),
            pasaporte: clientData.pasaporte ?? false,
          }}
          onSubmit={async (values) => {
            if (!captcha) {
              setIsLoading(false);
              return;
            }
            
            setIsLoading(true);
            try {
              const client: ClientSubmit = { ...values, concentimiento: values.concentimiento ? 1 : 0 };
              client.fechaNacimiento = dayjs(values.nacimiento).format('YYYY-MM-DD');
              client.rut = clientData.rut;
              client.celular = `+569${values.celular}`.replace(/\s+/g, '');
              const userNeedUpdate = isUserChange(clientData, client);
              await sleep(1000);
              
              if (!captcha) {
                setIsLoading(false);
                return;
              }

              if (!clientExists) {
                await createUser(dataHour, client, dataLabels);
              } else if (verificarMail || userNeedUpdate) {
                await updateUser(dataHour, client, dataLabels);
              } else {
                await reserveHour(dataHour, client, dataLabels);
              }
            } catch (error) {
              setIsLoading(false);
              console.error("Error procesando formulario:", error);
            }
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={ReservationSchema}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              {!clientData.nombre
                ? (
                  <>
                    <label className="labelForm"><b>Nombre</b></label>
                    <Field id="nombre" name="nombre" className="fieldForm" placeholder='Ingrese su nombre' />
                    {errors.nombre && touched.nombre ? (<div className="error">{String(errors.nombre)}</div>) : null}
                  </>
                )
                : null}
              {!clientData.paterno
                ? (
                  <>
                    <label className="labelForm"><b>Apellido Paterno</b></label>
                    <Field id="paterno" name="paterno" className="fieldForm" placeholder='Ingrese apellido paterno' />
                    {errors.paterno && touched.paterno ? (<div className="error">{String(errors.paterno)}</div>) : null}
                  </>
                )
                : null}
              {!clientData.materno
                ? (
                  <>
                    <label className="labelForm"><b>Apellido Materno</b></label>
                    <Field id="materno" name="materno" className="fieldForm" placeholder='Ingrese apellido materno' />
                    {errors.materno && touched.materno ? (<div className="error">{String(errors.materno)}</div>) : null}
                  </>
                )
                : null}
              {!clientData.sexo
                ? (
                  <>
                    <label className="labelForm"><b>Sexo</b></label>
                    <Select
                      placeholder="Selecciona un sexo"
                      className="formInput"
                      styles={colourStyles}
                      value={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }].find(o => o.value === values.sexo)}
                      options={[
                        { value: 'M', label: 'Masculino' },
                        { value: 'F', label: 'Femenino' },
                      ]}
                      onChange={(sexo: any) => {
                        setFieldValue('sexo', sexo.value);
                      }}
                    />
                  </>
                )
                : null}
              {!isValidNumber(clientData.ocupacion)
                ? (
                  <>
                    <label className="labelForm"><b>Ocupación</b></label>
                    <div
                      data-event="change_ocupacion"
                      data-value={values.ocupacion || ''}
                      style={{ display: 'contents' }}
                    >
                      <Select
                        placeholder="Selecciona una ocupación"
                        className="formInput"
                        styles={colourStyles}
                        value={[
                          { value: 1, label: 'Trabajador dependiente' },
                          { value: 2, label: 'Trabajador independiente' },
                          { value: 3, label: 'Estudiante' },
                          { value: 4, label: 'Sin ocupación' }
                        ].find(o => String(o.value) === String(values.ocupacion))}
                        options={[
                          { value: 1, label: 'Trabajador dependiente' },
                          { value: 2, label: 'Trabajador independiente' },
                          { value: 3, label: 'Estudiante' },
                          { value: 4, label: 'Sin ocupación' }
                        ]}
                        onChange={(ocupacion: any) => {
                          setFieldValue('ocupacion', ocupacion.value);
                        }}
                      />
                    </div>
                    {errors.ocupacion && touched.ocupacion ? (<div className="error">{String(errors.ocupacion)}</div>) : null}
                  </>
                )
                : null}
              {!isValidNumber(clientData.prevision)
                ? (
                  <>
                    <label className="labelForm"><b>Previsión</b></label>
                    <Select
                      placeholder="Selecciona una previsión"
                      className="formInput"
                      styles={colourStyles}
                      value={previsiones.map((item: any) => ({ value: item.codigo, label: item.nombre })).find(o => String(o.value) === String(values.prevision))}
                      options={previsiones.map((item: any) => ({ value: item.codigo, label: item.nombre }))}
                      onChange={(prevision: any) => {
                        setFieldValue('prevision', prevision.value);
                      }}
                    />
                    {errors.prevision && touched.prevision ? (<div className="error">{String(errors.prevision)}</div>) : null}
                  </>
                )
                : null}
              {!isValidNumber(clientData.nacionalidad)
                ? (
                  <>
                    <label className="labelForm"><b>Nacionalidad</b></label>
                    <Select
                      placeholder="Selecciona una nacionalidad"
                      className="formInput"
                      styles={colourStyles}
                      value={dataISOcountries.map((item: any) => ({ value: String(item.id), label: item.nombre })).find(o => String(o.value) === String(values.nacionalidad))}
                      options={dataISOcountries.map((item: any) => ({ value: String(item.id), label: item.nombre }))}
                      onChange={(nacionalidad: any) => {
                        setFieldValue('nacionalidad', nacionalidad.value);
                      }}
                    />
                    {errors.nacionalidad && touched.nacionalidad ? (<div className="error">{String(errors.nacionalidad)}</div>) : null}
                  </>
                )
                : null}
              {!clientData.direccion
                ? (
                  <>
                    <label className="labelForm"><b>Dirección</b></label>
                    <Field id="direccion" name="direccion" className="fieldForm" placeholder='Ej: General Freire 067' />
                    {errors.direccion && touched.direccion
                      ? (<div className="error">{String(errors.direccion)}</div>)
                      : null}
                  </>
                )
                : null}
              {!(isValidNumber((clientData as any).region) || isValidNumber((clientData as any).city))
                ? (
                  <>
                    <label className="labelForm"><b>Región</b></label>
                    <Select
                      placeholder="Selecciona una región"
                      className="formInput"
                      styles={colourStyles}
                      value={regiones.map((item: any) => ({ value: item.id_region, label: item.region })).find(o => String(o.value) === String(values.region))}
                      options={regiones.map((item: any) => ({ value: item.id_region, label: item.region }))}
                      onChange={(region: any) => {
                        setFieldValue('region', region.value);
                        setFieldValue('comuna', ''); 
                        setComunas([]); 
                        fetchCommunesByRegion(region.value); 
                      }}
                    />
                    {errors.region && touched.region
                      ? (<div className="error">{String(errors.region)}</div>)
                      : null}
                  </>
                )
                : null}

              {!(isValidNumber((clientData as any).comuna) || isValidNumber((clientData as any).state))
                ? (
                  <>
                    <label className="labelForm"><b>Comuna</b></label>
                    <Select
                      placeholder={values.region ? "Selecciona una comuna" : "Primero selecciona una región"}
                      className="formInput"
                      styles={colourStyles}
                      isDisabled={!values.region}
                      value={comunas.map((item: any) => ({ value: item.id, label: item.nombre })).find(o => String(o.value) === String(values.comuna))}
                      options={comunas.map((item: any) => ({ value: item.id, label: item.nombre }))}
                      onChange={(comuna: any) => {
                        setFieldValue('comuna', comuna.value);
                      }}
                    />
                    {errors.comuna && touched.comuna
                      ? (<div className="error">{String(errors.comuna)}</div>)
                      : null}
                  </>
                )
                : null}
                
              <>
                <label className="labelForm"><b>Fecha de nacimiento</b></label>
                <div
                  data-event="change_fecha_nacimiento"
                  data-value={values.nacimiento ? dayjs(values.nacimiento).format('YYYY-MM-DD') : ''}
                  style={{ display: 'contents' }}
                >
                  <MyDatePicker label="nacimiento" />
                </div>
                {errors.nacimiento && touched.nacimiento
                  ? (<div className="error">{String(errors.nacimiento)}</div>)
                  : null}
              </>
              <>
                <label className="labelForm"><b>Correo</b></label>
                <div className="dataField">
                  <div
                    data-event="input_email"
                    data-value={values.correo || ''}
                    style={{ display: 'contents' }}
                  >
                    <Field
                      id="correo"
                      name="correo"
                      className="fieldForm"
                      placeholder='correo@ejemplo.com'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newEmail = e.target.value;
                        setFieldValue('correo', newEmail);
                        if (newEmail !== initialEmail) {
                          setEmailVerified(false);
                          setVerificarMail(false);
                          setVerificationStep('initial');
                        }
                      }}
                    />
                  </div>
                  <ValidateEmailButton email={values.correo} />
                </div>
                {verificationStep === 'code_sent' && values.correo !== initialEmail && (
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '10px',
                    border: '1px solid #e9ecef',
                    marginBottom: '10px'
                  }}>
                    <label htmlFor="verificador" style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#495057',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <span style={{ marginRight: '8px' }}>🔒</span>
                      <b>Ingresa el código enviado a tu correo</b>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <Field
                        id="verificador"
                        name="verificador"
                        type="number"
                        style={{
                          width: '80%',
                          padding: '12px',
                          fontSize: '18px',
                          letterSpacing: '8px',
                          textAlign: 'center',
                          border: '2px solid #52A92B',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          setFieldValue('verificador', val);
                          setOtpError("");
                          if (val && String(val).length === 6) {
                            verifyOTP(String(val), values.correo);
                          }
                        }}
                      />
                    </div>
                    {otpError && (
                      <div className="error" style={{ marginTop: '8px' }}>{otpError}</div>
                    )}
                    {isValidatingOTP && (
                      <div style={{ marginTop: '8px', color: '#52A92B', fontSize: '12px', fontWeight: 'bold' }}>
                        <ClipLoader size={12} color="#52A92B" /> Validando código...
                      </div>
                    )}
                  </div>
                )}
                {errors.correo && touched.correo
                  ? (<div className="error">{String(errors.correo)}</div>)
                  : null}
                {!emailVerified && values.correo && !otpError && !isValidatingOTP &&
                  (<div className="error">{errorMessage ? errorMessage : "El correo debe ser validado"}</div>)}
                
                {/* TEXTO VERDE DE ÉXITO */}
                {emailVerified && verificationStep === 'verified' && values.correo !== initialEmail && (
                  <div style={{ color: '#52A92B', fontSize: '13px', fontWeight: 'bold', marginTop: '5px' }}>
                    ✓ Correo verificado correctamente
                  </div>
                )}
              </>
              <>
                <label className="labelForm"><b>Celular</b></label>
                <div className="dataField">
                  <p style={{ marginBottom: '3%' }}>+569</p>
                  <div
                    data-event="input_celular"
                    data-value={values.celular || ''}
                    style={{ display: 'contents' }}
                  >
                    <Field
                      id="celular"
                      name="celular"
                      className="formInput"
                      placeholder='87654321'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('celular', e.target.value);
                      }}
                    />
                  </div>
                </div>
                {errors.celular && touched.celular
                  ? (<div className="error">{String(errors.celular)}</div>)
                  : null}
              </>
              <div
                style={{ marginTop: '5%', marginBottom: '2%' }}
                data-event="click_recaptcha"
                data-value="validado"
              >
                <ReCAPTCHA
                  sitekey="6LfK0TIqAAAAACUjI8_cU1U0n1JuG6StQ--kMzsD"
                  onChange={(value) => {
                    handleChangeCaptcha(value);
                  }}
                  onExpired={handleCaptchaExpired}
                  onError={handleCaptchaError}
                />
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'row' }}
                data-event="toggle_politicas"
                data-value={userPermission ? 'checked' : 'unchecked'}
              >
                <Checkbox
                  style={{ color: '#52A92B' }}
                  value={userPermission}
                  onClick={() => {
                    const newValue = !userPermission;
                    setUserPermission(newValue);
                  }}
                  defaultChecked
                />
                <a href="https://maiposalud.cl/terminos-y-condiciones-de-uso-maiposalud.cl" target="_blank" style={{ textAlign: 'left', fontSize: '12px', marginTop: '1%', marginBottom: '1%' }}>
                  Acepto los términos y condiciones y la política de privacidad de Maiposalud.                </a>
              </div>

              <button
                className="reservationButton"
                type="submit"
                data-event="click_btn_reservationButton"
                data-value="submit_form"
                disabled={
                  isLoading ||
                  validatingMail ||
                  isValidatingOTP ||
                  !captcha ||
                  !userPermission ||
                  !emailVerified ||
                  !values.correo ||
                  (values.correo !== initialEmail && !verificarMail)
                }
              >
                {(isLoading)
                  ? <ClipLoader size={15} color="white" />
                  : 'Reservar Hora'
                }
              </button>
            </Form>
          )}
        </Formik>
      </div>
      <PopUp />
    </div>
  );
};

export default ClientDataContainer;